import { AxiosResponse } from 'axios';
import api from '../../../services/api';
import { USER_ROLE } from '../../../constants';
import { formatMessage } from '../../../i18n/translate';
import { CustumizableParameter } from '~/services/interfaces/user-interfaces';

const internalPath = '/user';

const roles = ['vendor', 'supervisor', 'manager', 'director', 'telemarketing'];
class UserService {
  getUsers(): Promise<AxiosResponse<any[]>> {
    return api.get<any[]>(internalPath);
  }

  getUser(_id: string): Promise<AxiosResponse<any>> {
    return api.get<any>(`${internalPath}/${_id}`);
  }

  createUser(user: any): Promise<AxiosResponse<any>> {
    return api.post<any>(internalPath, user);
  }

  editUser(user: any): Promise<AxiosResponse<any>> {
    return api.put<any>(internalPath, user);
  }

  deleteUser(_id: string): Promise<AxiosResponse<any>> {
    return api.delete(`${internalPath}/${_id}`);
  }

  putImage(userExternalCode: any, files: any): Promise<AxiosResponse<any>> {
    const formData = new FormData();
    formData.append('picture', files);

    return api.put<any>(`${internalPath}/picture?userExternalCode=${userExternalCode}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  getCustumizableParameters(): Promise<AxiosResponse<CustumizableParameter>> {
    return api.get<CustumizableParameter>(`${internalPath}/customizable-parameters`);
  }

  editDevice(userExternalCode: any, device: any): Promise<AxiosResponse<any>> {
    const { _id: deviceId, ...body } = device;
    return api.put<any>(`${internalPath}/${userExternalCode}/devices/${deviceId}`, body);
  }

  deleteDevice(userExternalCode: any, deviceId: any): Promise<AxiosResponse<any>> {
    return api.delete(`${internalPath}/${userExternalCode}/devices/${deviceId}`);
  }

  getHierarchyTree() {
    return api.get<any>(`${internalPath}/hierarchy-tree`);
  }

  parseUserRole(userRole: string) {
    switch (userRole) {
      case USER_ROLE.VENDOR:
        return formatMessage('userRoleVendor').toUpperCase();

      case USER_ROLE.SUPERVISOR:
        return formatMessage('userRoleSupervisor').toUpperCase();

      case USER_ROLE.MANAGER:
        return formatMessage('userRoleManager').toUpperCase();

      case USER_ROLE.DIRECTOR:
        return formatMessage('userRoleDirector').toUpperCase();

      case USER_ROLE.TECH:
        return formatMessage('userRoleTech').toUpperCase();

      case USER_ROLE.TELEMARKETING:
        return formatMessage('userRoleTelemarketing').toUpperCase();

      case USER_ROLE.MAINTENANCE:
        return formatMessage('userRoleMaintenance').toUpperCase();

      default:
        return formatMessage('userRoleVendor').toUpperCase();
    }
  }

  getUserRoles() {
    return [
      {
        key: USER_ROLE.VENDOR,
        label: formatMessage('userRoleVendor').toUpperCase()
      },
      {
        key: USER_ROLE.TELEMARKETING,
        label: formatMessage('userRoleTelemarketing').toUpperCase()
      },
      {
        key: USER_ROLE.SUPERVISOR,
        label: formatMessage('userRoleSupervisor').toUpperCase()
      },
      {
        key: USER_ROLE.MANAGER,
        label: formatMessage('userRoleManager').toUpperCase()
      },
      {
        key: USER_ROLE.DIRECTOR,
        label: formatMessage('userRoleDirector').toUpperCase()
      },
      {
        key: USER_ROLE.TECH,
        label: formatMessage('userRoleTech').toUpperCase()
      },
      {
        key: USER_ROLE.MAINTENANCE,
        label: formatMessage('userRoleMaintenance').toUpperCase()
      }
    ];
  }

  getInitialSellers = (sellers: any[], user: any) => {
    if (user?.userRole === USER_ROLE.VENDOR || user?.userRole === USER_ROLE.TELEMARKETING) {
      return [user?.externalCode];
    } else {
      const recursivo = (sellers: any[]) => {
        return sellers.reduce((array, seller) => {
          if (seller.userRole === USER_ROLE.VENDOR || seller.userRole === USER_ROLE.TELEMARKETING) {
            return [...array, seller?.externalCode];
          } else {
            if (seller?.externalCode && seller?.externalCode !== '') {
              return [...array, seller?.externalCode, recursivo(seller?.sellers)];
            } else {
              return [...array, recursivo(seller?.sellers)];
            }
          }
        }, []);
      };

      return recursivo(sellers).flat(Infinity);
    }
  };

  buildSellersLevel = (sellers: any[], user: any) => {
    const getSellersBellow = (superior: any) => {
      const nextUserRole = roles[roles.indexOf(superior?.userRole) - 1];

      const nextUserRoles =
        nextUserRole === USER_ROLE.VENDOR ? [USER_ROLE.VENDOR, USER_ROLE.TELEMARKETING] : [nextUserRole];

      const sellersBellow = sellers.filter(
        v => v.superiorExternalCode === superior.roleExternalCode && nextUserRoles.includes(v.userRole)
      );

      if (
        superior.userRole === USER_ROLE.VENDOR ||
        superior.userRole === USER_ROLE.TELEMARKETING ||
        sellersBellow.length === 0
      ) {
        return {
          ...superior,
          selected: true,
          sellers: []
        };
      } else {
        return {
          ...superior,
          selected: true,
          sellers: [...sellersBellow].map(y => ({ ...getSellersBellow(y) }))
        };
      }
    };

    return sellers
      ?.filter((seller: any) => {
        if (user?.userRole === USER_ROLE.DIRECTOR) {
          return seller.userRole === USER_ROLE.MANAGER;
        } else if (user?.userRole === USER_ROLE.MANAGER) {
          return seller.userRole === USER_ROLE.SUPERVISOR && seller.superiorExternalCode === user.roleExternalCode;
        } else {
          return seller.superiorExternalCode === user.roleExternalCode;
        }
      })
      .map(x => ({ ...getSellersBellow(x) }));
  };

  getMe() {
    return api.get<any>(`${internalPath}/me`);
  }

  getCommissions(userExternalCode: string) {
    return api.get(`${internalPath}/commissions`, {
      params: { userExternalCode }
    });
  }
}

export default new UserService();
